import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, RefreshCw, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const toText = (v) => (v == null ? '' : String(v));

const formatDateTime = (raw) => {
  const s = toText(raw).trim();
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('id-ID');
};

const formatShortId = (id) => {
  const s = toText(id).trim();
  if (!s) return '-';
  return s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
};

const safeJsonString = (obj) => {
  try {
    return JSON.stringify(obj ?? null, null, 2);
  } catch (_) {
    return toText(obj);
  }
};

const AdminAuditLogs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const [filters, setFilters] = useState({
    actor_user_id: '',
    actor_role: '',
    entity_type: '',
    action: '',
    start_date: '',
    end_date: '',
    search: '',
  });

  const queryParams = useMemo(() => {
    const params = {
      limit,
      offset,
    };
    Object.entries(filters).forEach(([k, v]) => {
      const s = toText(v).trim();
      if (s) params[k] = s;
    });
    return params;
  }, [filters, limit, offset]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/admin/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/audit-logs', { params: queryParams });
      const payload = res?.data || {};
      setLogs(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total || 0));
      setHasMore(Boolean(payload.has_more));
    } catch (e) {
      toast.error('Gagal memuat audit logs');
      setLogs([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        toast.error('Akses ditolak');
        navigate('/admin/login');
        return;
      }
    } catch (_) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    loadUsers();
  }, [navigate, loadUsers]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const toggleExpanded = (id) => {
    const s = toText(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const resetFilters = () => {
    setFilters({
      actor_user_id: '',
      actor_role: '',
      entity_type: '',
      action: '',
      start_date: '',
      end_date: '',
      search: '',
    });
    setOffset(0);
    setExpandedIds(new Set());
  };

  const onChangeFilter = (key) => (e) => {
    const value = e?.target?.value ?? '';
    setOffset(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const gotoPrev = () => {
    setExpandedIds(new Set());
    setOffset((o) => Math.max(0, o - limit));
  };

  const gotoNext = () => {
    setExpandedIds(new Set());
    setOffset((o) => o + limit);
  };

  const currentPage = total > 0 ? Math.floor(offset / limit) + 1 : 1;
  const totalPages = total > 0 ? Math.ceil(total / limit) : 1;

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </Link>
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Audit Logs</h1>
              <div className="text-sm text-zinc-400 mt-2">
                Total: <span className="text-white font-bold">{Number(total || 0).toLocaleString('id-ID')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadLogs()}
                className="bg-transparent border border-zinc-700 text-zinc-200 hover:border-primary/60 hover:text-white px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={resetFilters}
                className="bg-transparent border border-zinc-700 text-zinc-200 hover:border-primary/60 hover:text-white px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Reset Filter
              </button>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">User</div>
                <select
                  value={filters.actor_user_id}
                  onChange={onChangeFilter('actor_user_id')}
                  disabled={loadingUsers}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                >
                  <option value="">{loadingUsers ? 'Loading…' : 'Semua'}</option>
                  {(users || []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} • {u.role} • {u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Action</div>
                <input
                  value={filters.action}
                  onChange={onChangeFilter('action')}
                  placeholder="contoh: create / update / verify"
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                />
              </div>

              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Entity Type</div>
                <input
                  value={filters.entity_type}
                  onChange={onChangeFilter('entity_type')}
                  placeholder="contoh: booking / payment / expense"
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                />
              </div>

              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Role</div>
                <select
                  value={filters.actor_role}
                  onChange={onChangeFilter('actor_role')}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                >
                  <option value="">Semua</option>
                  <option value="admin">admin</option>
                  <option value="kasir">kasir</option>
                  <option value="customer">customer</option>
                </select>
              </div>

              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Start Date</div>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={onChangeFilter('start_date')}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                />
              </div>

              <div>
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">End Date</div>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={onChangeFilter('end_date')}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                />
              </div>

              <div className="md:col-span-2">
                <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Search</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={filters.search}
                      onChange={onChangeFilter('search')}
                      placeholder="entity_id / entity_type / action / role / ip / user agent"
                      className="w-full bg-black border border-zinc-800 rounded-md pl-10 pr-3 py-2 text-white outline-none focus:border-primary"
                    />
                  </div>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setOffset(0);
                      setExpandedIds(new Set());
                      setLimit(Number(e?.target?.value || 50));
                    }}
                    className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white outline-none focus:border-primary"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            {loading ? (
              <div className="text-center py-10">
                <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : logs.length ? (
              <div className="space-y-4">
                {logs.map((log) => {
                  const id = toText(log?.id);
                  const expanded = expandedIds.has(id);
                  const actorLabel = log?.actor_name ? `${log.actor_name}${log?.actor_role ? ` • ${log.actor_role}` : ''}` : (log?.actor_role || '-');
                  return (
                    <div key={id} className="bg-black border border-zinc-800 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleExpanded(id)}
                        className="w-full text-left px-5 py-4 hover:bg-zinc-950/60 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="text-white font-black uppercase tracking-wide">
                              {toText(log?.action || '-')} • {toText(log?.entity_type || '-')}
                            </div>
                            <div className="text-xs text-zinc-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span>{formatDateTime(log?.created_at)}</span>
                              <span>Entity: {formatShortId(log?.entity_id)}</span>
                              <span>Actor: {actorLabel}</span>
                              {log?.ip_address ? <span>IP: {log.ip_address}</span> : null}
                            </div>
                            {log?.user_agent ? (
                              <div className="text-xs text-zinc-600 mt-2 truncate">
                                {toText(log.user_agent)}
                              </div>
                            ) : null}
                          </div>
                          <div className="text-zinc-400 shrink-0 mt-1">
                            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </div>
                      </button>

                      {expanded ? (
                        <div className="border-t border-zinc-800 px-5 py-4">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="border border-zinc-800 rounded-lg p-4">
                              <div className="text-xs text-zinc-500 font-bold uppercase mb-2">Before</div>
                              <pre className="text-xs text-zinc-200 whitespace-pre-wrap break-words">
                                {safeJsonString(log?.before_data)}
                              </pre>
                            </div>
                            <div className="border border-zinc-800 rounded-lg p-4">
                              <div className="text-xs text-zinc-500 font-bold uppercase mb-2">After</div>
                              <pre className="text-xs text-zinc-200 whitespace-pre-wrap break-words">
                                {safeJsonString(log?.after_data)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-zinc-500">Tidak ada audit log.</div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
              <div className="text-sm text-zinc-500">
                Page <span className="text-white font-bold">{currentPage}</span> /{' '}
                <span className="text-white font-bold">{totalPages}</span>
                <span className="ml-3">Offset: {offset.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={gotoPrev}
                  disabled={offset <= 0}
                  className="bg-transparent border border-zinc-700 text-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary/60 hover:text-white px-4 py-2 rounded-md font-bold uppercase text-sm transition-all"
                >
                  Prev
                </button>
                <button
                  onClick={gotoNext}
                  disabled={!hasMore}
                  className="bg-transparent border border-zinc-700 text-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary/60 hover:text-white px-4 py-2 rounded-md font-bold uppercase text-sm transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
